/**
 * Coach Client Detail — Full client profile view for coaches.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, RefreshControl, Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { resolveProfileById } from '@/lib/trainers';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import DirectMessage, { getUnreadCount } from '@/components/coach/DirectMessage';
import StrengthBreakdown from '@/components/coach/StrengthBreakdown';
import ProgressChart from '@/components/coach/ProgressChart';
import ComplianceRate from '@/components/coach/ComplianceRate';
import CoachingNoteEditor from '@/components/coach/CoachingNoteEditor';
import ProgressPhotos from '@/components/coach/ProgressPhotos';
import CheckInConfigModal, { CheckInSummary } from '@/components/coach/CheckInConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { resolveCoachOwnerOrg } from '@/lib/orgProvisioning';
import { useCoachAccess } from '@/lib/coachAccess';
import { fetchCoachNotes, addCoachNote, deleteCoachNote, type CoachNote } from '@/lib/coachNotes';
import { loadPrograms, assignProgram, getAssignedPrograms, type Program } from '@/lib/programBuilder';
import {
  ArrowLeft, Mail, Phone, MessageSquare,
  ClipboardList, Calendar, Trophy, Dumbbell, Activity,
  FileText, Trash2, Camera, ClipboardCheck,
} from 'lucide-react-native';

interface ClientProfile {
  id: string;
  display_name: string;
  username: string;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  strength_score: number;
  freshness_band: string;
  baseline_complete_pct: number;
}

interface WorkoutSummary {
  id: string;
  performed_at: string;
  exercises: string[];
  total_volume: number;
}

interface PRRecord {
  lift_key: string;
  weight: number;
  reps: number;
  date: string;
}

export default function CoachClientDetail() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { clientId, clientName, clientHandle, strengthScore, freshnessBand, baselinePct } = useLocalSearchParams<{
    clientId: string;
    clientName?: string;
    clientHandle?: string;
    strengthScore?: string;
    freshnessBand?: string;
    baselinePct?: string;
  }>();

  const [client, setClient] = useState<ClientProfile | null>(null);
  const [workouts, setWorkouts] = useState<WorkoutSummary[]>([]);
  const [prs, setPrs] = useState<PRRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDM, setShowDM] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [coachOrgId, setCoachOrgId] = useState<string | null>(null);
  const [coachPrograms, setCoachPrograms] = useState<Program[]>([]);
  const [assignedPrograms, setAssignedPrograms] = useState<Program[]>([]);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [assigningProgram, setAssigningProgram] = useState(false);
  const { profile } = useAuth();
  const { tier } = useSubscription();

  // Route guard: coach_pro (or org_pro) required.
  // Unauthorized users are redirected to home before any client data is loaded.
  const { hasAccess, isLoading: accessLoading } = useCoachAccess();
  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      router.replace('/(tabs)/home');
    }
  }, [accessLoading, hasAccess]);

  const loadClient = useCallback(async () => {
    if (!clientId) return;
    try {
      // Verify coaching relationship before loading any client data
      const coachId = profile?.id;
      if (!coachId) {
        Alert.alert('Access Denied', 'You must be logged in to view client details.');
        router.back();
        return;
      }
      const { data: link } = await supabase
        .from('coach_athlete_links')
        .select('id')
        .eq('coach_user_id', coachId)
        .eq('athlete_user_id', clientId)
        .eq('status', 'active')
        .maybeSingle();

      if (!link) {
        Alert.alert('Access Denied', 'You do not have an active coaching relationship with this client.');
        router.back();
        return;
      }

      // Profile
      const pub = await resolveProfileById(clientId);
      const { data: pd } = await supabase
        .from('profiles')
        .select('id, email, phone, strength_score, freshness_band, baseline_complete_pct')
        .eq('id', clientId)
        .single();

      setClient({
        id: clientId,
        display_name: pub?.display_name || pub?.username || clientName || 'Unknown',
        username: pub?.username || clientHandle || '',
        avatar_url: null,
        email: pd?.email ?? null,
        phone: pd?.phone ?? null,
        strength_score: pd?.strength_score ?? (strengthScore ? Number(strengthScore) : 0),
        freshness_band: pd?.freshness_band ?? freshnessBand ?? 'gray',
        baseline_complete_pct: pd?.baseline_complete_pct ?? (baselinePct ? Number(baselinePct) : 0),
      });

      // Recent workouts
      const { data: wData } = await supabase
        .from('workouts')
        .select('id, performed_at, exercises, total_volume')
        .eq('user_id', clientId)
        .order('performed_at', { ascending: false })
        .limit(10);

      if (wData) {
        setWorkouts(wData.map((w: any) => ({
          id: w.id,
          performed_at: w.performed_at,
          exercises: Array.isArray(w.exercises)
            ? w.exercises.map((e: any) => typeof e === 'string' ? e : e.name || e.exercise_name || 'Unknown')
            : [],
          total_volume: w.total_volume ?? 0,
        })));
      }

      // PRs from strength_events
      const { data: prData } = await supabase
        .from('strength_events')
        .select('lift_key, weight, reps, created_at')
        .eq('profile_id', clientId)
        .eq('is_pr', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (prData) {
        // Keep best per exercise
        const best = new Map<string, PRRecord>();
        for (const p of prData as any[]) {
          const key = p.lift_key;
          if (!best.has(key) || p.weight > best.get(key)!.weight) {
            best.set(key, {
              lift_key: p.lift_key,
              weight: p.weight,
              reps: p.reps ?? 1,
              date: p.created_at,
            });
          }
        }
        setPrs(Array.from(best.values()));
      }
      // Coach notes
      if (profile?.id) {
        const notesList = await fetchCoachNotes(profile.id, clientId);
        setNotes(notesList);
        // Unread message count
        const unread = await getUnreadCount(profile.id, clientId);
        setUnreadCount(unread);
        // Programs: coach's programs + client's assigned programs
        const [myProgs, clientProgs] = await Promise.all([
          loadPrograms(profile.id),
          getAssignedPrograms(clientId),
        ]);
        setCoachPrograms(myProgs);
        setAssignedPrograms(clientProgs);
      }
    } catch (err) {
      console.warn('[CoachClientDetail] load error', err);
    }
  }, [clientId, profile?.id]);

  // Resolve coach org ID (needed for addCoachNote org_id)
  useEffect(() => {
    if (!profile?.id) return;
    resolveCoachOwnerOrg(profile.id)
      .then((id) => { if (id) setCoachOrgId(id); })
      .catch(() => {});
  }, [profile?.id]);

  useEffect(() => {
    setLoading(true);
    loadClient().finally(() => setLoading(false));
  }, [loadClient]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadClient();
    setRefreshing(false);
  };

  const FRESHNESS_COLORS: Record<string, string> = {
    green: '#22c55e', yellow: '#eab308', red: '#ef4444', gray: '#6b7280',
  };

  // Guard: render nothing while access check is in progress or unauthorized
  if (accessLoading || !hasAccess) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  if (!client) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
        <Text style={{ color: c.text, textAlign: 'center', marginTop: 60 }}>Client not found</Text>
      </SafeAreaView>
    );
  }

  const initial = (client.display_name || '?')[0].toUpperCase();
  const dotColor = FRESHNESS_COLORS[client.freshness_band] || FRESHNESS_COLORS.gray;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
      {/* Top bar */}
      <View style={styles.topBar}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.topTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
          CLIENT DETAIL
        </Text>
        <View style={{ width: 22 }} />
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
      >
        {/* Header */}
        <View style={styles.headerSection}>
          <View style={[styles.avatar, { backgroundColor: c.accent + '22' }]}>
            <Text style={[styles.avatarText, { color: c.accent }]}>{initial}</Text>
          </View>
          <Text style={[styles.name, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            {client.display_name}
          </Text>
          <Text style={[styles.handle, { color: c.textSecondary }]}>@{client.username}</Text>
        </View>

        {/* Contact */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            CONTACT
          </Text>
          <View style={styles.contactRow}>
            <Mail size={14} color={c.textSecondary} />
            <Text style={[styles.contactText, { color: c.text }]}>{client.email || '—'}</Text>
          </View>
          {!!client.phone && (
            <View style={styles.contactRow}>
              <Phone size={14} color={c.textSecondary} />
              <Text style={[styles.contactText, { color: c.text }]}>{client.phone}</Text>
            </View>
          )}
        </View>

        {/* Stats */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            STATS
          </Text>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Dumbbell size={16} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{client.strength_score}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Strength</Text>
            </View>
            <View style={styles.statItem}>
              <Activity size={16} color={dotColor} />
              <Text style={[styles.statValue, { color: c.text }]}>{client.freshness_band}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Freshness</Text>
            </View>
            <View style={styles.statItem}>
              <Trophy size={16} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{client.baseline_complete_pct}%</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Baseline</Text>
            </View>
          </View>
        </View>

        {/* Strength Breakdown */}
        <StrengthBreakdown clientId={clientId!} />

        {/* Progress Chart */}
        <ProgressChart clientId={clientId!} />

        {/* Compliance Rate (editable target) */}
        <ComplianceRate clientId={clientId!} editable />

        {/* Quick Actions */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            ACTIONS
          </Text>
          <View style={styles.actionsRow}>
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: c.accent }]} onPress={() => { setShowDM(true); setUnreadCount(0); }}>
              <MessageSquare size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Message</Text>
              {unreadCount > 0 && (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadText}>{unreadCount}</Text>
                </View>
              )}
            </TouchableOpacity>
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: c.accent }]} onPress={() => router.push({ pathname: '/coach-dashboard', params: { tab: 'programs', assignTo: clientId, assignToName: client.display_name } } as any)}>
              <ClipboardList size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Program</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: c.accent }]} onPress={() => router.push({ pathname: '/coach-schedule', params: { prefillClientId: clientId, prefillClientName: client.display_name } } as any)}>
              <Calendar size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Schedule</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.actionsRow, { marginTop: 8 }]}>
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: c.accent }]} onPress={() => setShowPhotos(true)}>
              <Camera size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Photos</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.actionBtn, { backgroundColor: c.accent }]} onPress={() => setShowCheckIn(true)}>
              <ClipboardCheck size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Configure</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, { backgroundColor: c.accent }]}
              onPress={() => router.push({
                pathname: '/coach-checkin-responses',
                params: { clientId: clientId ?? '', clientName: client.display_name },
              } as any)}
            >
              <ClipboardCheck size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Check-Ins</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Recent Workouts */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            RECENT WORKOUTS
          </Text>
          {workouts.length === 0 ? (
            <Text style={[styles.emptyText, { color: c.textTertiary }]}>No workouts yet</Text>
          ) : (
            workouts.map((w) => (
              <View key={w.id} style={[styles.workoutRow, { borderColor: c.border }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.workoutExercises, { color: c.text }]} numberOfLines={1}>
                    {w.exercises.length > 0 ? w.exercises.join(', ') : 'Workout'}
                  </Text>
                  <Text style={[styles.workoutMeta, { color: c.textSecondary }]}>
                    {new Date(w.performed_at).toLocaleDateString()} · {Math.round(w.total_volume).toLocaleString()} vol
                  </Text>
                </View>
              </View>
            ))
          )}
        </View>

        {/* PR History */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            PR HISTORY
          </Text>
          {prs.length === 0 ? (
            <Text style={[styles.emptyText, { color: c.textTertiary }]}>No PRs recorded</Text>
          ) : (
            prs.map((pr, i) => (
              <View key={`${pr.lift_key}-${i}`} style={[styles.prRow, { borderColor: c.border }]}>
                <Text style={[styles.prExercise, { color: c.text }]}>{pr.lift_key}</Text>
                <Text style={[styles.prWeight, { color: c.accent }]}>
                  {pr.weight} lbs × {pr.reps}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* Program Assignment */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            PROGRAM
          </Text>
          {assignedPrograms.length > 0 ? (
            assignedPrograms.map((prog) => (
              <View key={prog.id} style={[styles.programRow, { borderColor: c.border }]}>
                <ClipboardList size={16} color={c.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.workoutExercises, { color: c.text }]}>{prog.name}</Text>
                  <Text style={[styles.workoutMeta, { color: c.textSecondary }]}>
                    {prog.days.length} day{prog.days.length !== 1 ? 's' : ''}
                  </Text>
                </View>
              </View>
            ))
          ) : (
            <Text style={[styles.emptyText, { color: c.textTertiary }]}>No program assigned</Text>
          )}
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: c.accent, marginTop: 10 }]}
            onPress={() => setShowProgramPicker(true)}
          >
            <ClipboardList size={16} color="#fff" />
            <Text style={styles.actionBtnText}>
              {assignedPrograms.length > 0 ? 'Change Program' : 'Assign Program'}
            </Text>
          </TouchableOpacity>
          {/* Program picker modal */}
          {showProgramPicker && (
            <View style={[styles.pickerOverlay, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.sectionTitle, { color: c.text, marginBottom: 8 }]}>SELECT PROGRAM</Text>
              {coachPrograms.length === 0 ? (
                <Text style={[styles.emptyText, { color: c.textTertiary }]}>No programs yet. Create one from the dashboard.</Text>
              ) : (
                coachPrograms.map((prog) => (
                  <TouchableOpacity
                    key={prog.id}
                    style={[styles.pickerRow, { borderColor: c.border }]}
                    onPress={async () => {
                      if (!clientId) return;
                      setAssigningProgram(true);
                      await assignProgram(prog.id, clientId);
                      const refreshed = await getAssignedPrograms(clientId);
                      setAssignedPrograms(refreshed);
                      setAssigningProgram(false);
                      setShowProgramPicker(false);
                    }}
                    disabled={assigningProgram}
                  >
                    <Text style={{ color: c.text, fontSize: 14, fontFamily: TYPO.body.semibold }}>{prog.name}</Text>
                    <Text style={{ color: c.textSecondary, fontSize: 12 }}>{prog.days.length} days</Text>
                  </TouchableOpacity>
                ))
              )}
              <TouchableOpacity onPress={() => setShowProgramPicker(false)} style={{ marginTop: 10, alignItems: 'center' }}>
                <Text style={{ color: c.textTertiary, fontSize: 13 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Check-in Summary */}
        {profile?.id && <CheckInSummary coachId={profile.id} clientId={clientId!} />}

        {/* Coach Notes */}
        <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text, fontFamily: TYPO.heading.semibold }]}>
            COACH NOTES
          </Text>

          {/* Existing notes */}
          {notes.length > 0 && notes.map((note) => (
            <View key={note.id} style={[styles.noteItem, { borderColor: c.border }]}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.noteText, { color: c.text }]}>{note.note}</Text>
                <Text style={[styles.noteMeta, { color: c.textTertiary }]}>
                  {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
              </View>
              <TouchableOpacity
                onPress={async () => {
                  const success = await deleteCoachNote(note.id);
                  if (success) setNotes((prev) => prev.filter((n) => n.id !== note.id));
                }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Trash2 size={14} color={c.textTertiary} />
              </TouchableOpacity>
            </View>
          ))}

          {/* Add note */}
          <CoachingNoteEditor
            clientId={clientId!}
            onSave={async (noteData) => {
              if (!profile?.id) return;
              // Use coachOrgId (from personal coaching org) — NOT profile.gym_id
              // profile.gym_id is a gyms table FK, not an organizations UUID (different namespace)
              const newNote = await addCoachNote(
                coachOrgId,
                profile.id,
                clientId!,
                noteData.content,
              );
              if (newNote) {
                setNotes((prev) => [newNote, ...prev]);
              }
            }}
          />
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      {/* DM Modal */}
      <DirectMessage
        visible={showDM}
        clientId={clientId ?? ''}
        clientName={client.display_name}
        onClose={() => setShowDM(false)}
      />

      {/* Progress Photos Modal */}
      <ProgressPhotos
        visible={showPhotos}
        clientId={clientId ?? ''}
        clientName={client.display_name}
        canUpload
        onClose={() => setShowPhotos(false)}
      />

      {/* Check-in Config Modal */}
      {profile?.id && (
        <CheckInConfigModal
          visible={showCheckIn}
          coachId={profile.id}
          clientId={clientId ?? ''}
          clientName={client.display_name}
          onClose={() => setShowCheckIn(false)}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  topTitle: {
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  scroll: {
    paddingHorizontal: 16,
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: 20,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  avatarText: {
    fontSize: 28,
    fontWeight: '800',
  },
  name: {
    fontSize: 22,
    fontWeight: '900',
  },
  handle: {
    fontSize: 14,
    fontWeight: '500',
    marginTop: 2,
  },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 1,
    marginBottom: 12,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  contactText: {
    fontSize: 14,
    fontWeight: '500',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    gap: 4,
  },
  statValue: {
    fontSize: 16,
    fontWeight: '800',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: '600',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 2,
  },
  actionBtnText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  unreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: '#ef4444',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  unreadText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },
  workoutRow: {
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  workoutExercises: {
    fontSize: 14,
    fontWeight: '600',
  },
  workoutMeta: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
  },
  prRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  prExercise: {
    fontSize: 14,
    fontWeight: '600',
  },
  prWeight: {
    fontSize: 14,
    fontWeight: '800',
  },
  emptyText: {
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 12,
  },
  noteItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    marginBottom: 8,
  },
  noteText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  noteMeta: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 4,
  },
  programRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  pickerOverlay: {
    marginTop: 12,
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
  },
  pickerRow: {
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
});
